/**
 * Which snapshots the UI accepts.
 *
 * The snapshot *is* the client state, so the one rule that matters is that a
 * late or duplicated one must never regress the table. The watermark is per
 * *table*, because `seq` counts actions within a match and restarts at 0 for a
 * new one: a single watermark across the session meant the second table of an
 * evening had every snapshot discarded as stale while the UI went on rendering
 * the first, recoverable only by reloading the page (VERDICT B6).
 *
 * One table can also play more than one match — "play again with the same
 * seats" — and that restarts `seq` at 0 without changing the table id. Keying
 * on the table alone therefore discarded every snapshot of a rematch as stale
 * and left the players looking at the end screen of the match before it. So the
 * watermark is scoped to the match as well, and a new match starts a new count.
 *
 * `startedAt` identifies the match rather than `matchId`, because a spectator's
 * snapshot carries no match id at all (it is blanked on the way out, see
 * `Table.withoutMatchIdForSpectators`) and a spectator watching a rematch has
 * exactly the same problem.
 *
 * Lifted out of the hook so the rule can be tested as a rule rather than
 * through a socket and a renderer.
 */
import type { TableSnapshot } from '@cards/shared'

/** A long evening of rematches would otherwise grow the map without bound. */
const MAX_TABLES = 16

interface Mark {
  /** Which match the `seq` belongs to; null while the table is a lobby. */
  readonly match: number | null
  readonly seq: number
}

export class SnapshotGate {
  private readonly seen = new Map<string, Mark>()

  /** True when this snapshot is newer than, or level with, what the table has
   *  already shown — and therefore should be rendered. Equal `seq` is accepted
   *  because room-only changes (a seat taken, a bot added) do not advance the
   *  match sequence and sit at seq 0 for the whole lobby. */
  accept(next: TableSnapshot): boolean {
    const tableId = next.room?.tableId ?? ''
    const match = next.match?.startedAt ?? null
    const mark = this.seen.get(tableId)
    // A different match at the same table is a different sequence, and nothing
    // it sends can be stale with respect to the one before it.
    if (mark !== undefined && mark.match === match && next.seq < mark.seq) return false
    this.seen.set(tableId, { match, seq: next.seq })
    if (this.seen.size > MAX_TABLES) {
      const oldest = this.seen.keys().next().value
      if (oldest !== undefined && oldest !== tableId) this.seen.delete(oldest)
    }
    return true
  }

  /** Test seam: how many tables the gate is remembering. */
  get size(): number {
    return this.seen.size
  }
}
